import type { RightPanelSurfaceTab } from "./rightPanelSurfaceTabs";

/**
 * The actions offered on a right-panel surface tab.
 *
 * Ported from t3code's `RightPanelTabs`, which offers close/mute/rename
 * through the Electron native context menu. Forge renders them with the Base
 * UI `Menu` instead, so the capability works on web as well as desktop. The
 * menu is not the part worth copying; the actions are.
 */
export type RightPanelTabCloseAction = "close" | "close-others" | "close-to-right" | "close-all";
export type RightPanelTabAction = "rename" | "toggle-mute" | RightPanelTabCloseAction;

export interface RightPanelTabMenuItem {
  readonly action: RightPanelTabAction;
  readonly label: string;
  /** True when the action would do nothing, so the item reads as inert. */
  readonly disabled: boolean;
}

/**
 * Which surfaces a close action closes, in the order they should be closed.
 *
 * Returns ids rather than performing the close because the caller closes one
 * surface at a time: resolving the whole list up front means the answer cannot
 * shift underneath a partially-applied bulk close. `close-to-right` on a strip
 * that reorders itself after each removal would otherwise walk off the end or
 * take the wrong tabs.
 *
 * An unknown `surfaceId` yields an empty list rather than throwing — a tab can
 * be closed by something else while its menu is open, and a menu click landing
 * on a surface that has since gone is a no-op, not an error.
 */
export function resolveRightPanelTabCloseTargets(input: {
  readonly action: RightPanelTabCloseAction;
  readonly tabs: ReadonlyArray<RightPanelSurfaceTab>;
  readonly surfaceId: string;
}): ReadonlyArray<string> {
  const { action, tabs, surfaceId } = input;

  if (action === "close-all") return tabs.map((tab) => tab.id);

  const index = tabs.findIndex((tab) => tab.id === surfaceId);
  if (index === -1) return [];

  switch (action) {
    case "close":
      return [surfaceId];
    case "close-others":
      return tabs.filter((tab) => tab.id !== surfaceId).map((tab) => tab.id);
    case "close-to-right":
      return tabs.slice(index + 1).map((tab) => tab.id);
  }
}

/**
 * The menu for one tab: the capabilities the tab actually has, then the four
 * close actions in t3code's order.
 *
 * Rename and mute are offered only when the tab has them: a menu item that
 * is present but does nothing is worse than an absent one. Each close item
 * is disabled when it would close nothing. Disabling is computed from
 * the same target resolution the click uses, so an enabled item can never be
 * a no-op and a disabled one can never have had work to do.
 */
export function buildRightPanelTabMenu(input: {
  readonly tabs: ReadonlyArray<RightPanelSurfaceTab>;
  readonly surfaceId: string;
  /** The tab can be named by the user; offered only then. */
  readonly renamable: boolean;
  /** A desktop browser view exists behind the tab to mute; offered only then. */
  readonly canToggleMute: boolean;
  readonly muted: boolean;
}): ReadonlyArray<RightPanelTabMenuItem> {
  const closeLabel: Record<RightPanelTabCloseAction, string> = {
    close: "Close",
    "close-others": "Close others",
    "close-to-right": "Close to the right",
    "close-all": "Close all",
  };

  const items: RightPanelTabMenuItem[] = [];
  if (input.renamable) {
    items.push({ action: "rename", label: "Rename", disabled: false });
  }
  if (input.canToggleMute) {
    items.push({
      action: "toggle-mute",
      label: input.muted ? "Unmute tab" : "Mute tab",
      disabled: false,
    });
  }
  for (const action of ["close", "close-others", "close-to-right", "close-all"] as const) {
    items.push({
      action,
      label: closeLabel[action],
      disabled:
        resolveRightPanelTabCloseTargets({
          tabs: input.tabs,
          surfaceId: input.surfaceId,
          action,
        }).length === 0,
    });
  }
  return items;
}
